<template>
  <transition name="modal-fade" @before-enter="onBeforeEnter">
    <div
      v-if="show"
      ref="modalRoot"
      :class="modalClassName"
      transition="modal"
      @click="onBackDropClick"
    >
      <div :class="modalContainerClassName" @click.stop>
        <woot-button
          v-if="showCloseButton"
          color-scheme="secondary"
          icon="dismiss"
          variant="clear"
          class="modal--close"
          @click="close"
        />
        <slot />
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  props: {
    closeOnBackdropClick: {
      type: Boolean,
      default: true,
    },
    show: Boolean,
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    modalType: {
      type: String,
      default: 'centered',
    },
    size: {
      type: String,
      default: '',
    },
    contentClass: {
      type: String,
      default: '',
    },
    portalToBody: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      portalReady: !this.portalToBody,
    };
  },
  computed: {
    modalContainerClassName() {
      let className =
        // 'modal-container bg-white dark:bg-slate-800 skip-context-menu';
        'modal-container bg-white dark:bg-slate-800 skip-context-menu';
      if (this.fullWidth) {
        return `${className} modal-container--full-width`;
      }

      return [className, this.size, this.contentClass]
        .filter(Boolean)
        .join(' ');
    },
    modalClassName() {
      const modalClassNameMap = {
        centered: '',
        'right-aligned': 'right-aligned',
      };

      const portalClasses = this.portalToBody
        ? [
            'modal-mask--body-portal',
            this.portalReady ? 'modal-mask--body-portal-ready' : '',
          ]
        : [];

      return [
        'modal-mask skip-context-menu',
        ...portalClasses,
        modalClassNameMap[this.modalType] || '',
      ]
        .filter(Boolean)
        .join(' ');
    },
  },
  mounted() {
    this.onKeydown = e => {
      if (this.show && e.code === 'Escape') {
        this.onClose();
      }
    };
    document.addEventListener('keydown', this.onKeydown);
    if (this.portalToBody && this.show) {
      this.$nextTick(() => this.moveModalToBody());
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(visible) {
        if (!this.portalToBody) return;
        if (!visible) {
          this.portalReady = false;
          return;
        }
        this.$nextTick(() => this.moveModalToBody());
      },
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    this.restorePortalDom();
  },
  methods: {
    close() {
      this.onClose();
    },
    onBackDropClick() {
      if (this.closeOnBackdropClick) {
        this.onClose();
      }
    },
    onBeforeEnter(el) {
      if (this.portalToBody) {
        this.moveModalToBody(el);
      }
    },
    moveModalToBody(el) {
      const modalEl = el || this.$refs.modalRoot;
      if (!modalEl) {
        if (this.portalToBody && this.show) {
          this.$nextTick(() => this.moveModalToBody());
        }
        return;
      }
      if (modalEl.parentNode === document.body) {
        this.portalReady = true;
        return;
      }

      this._portalOrigin = {
        parent: modalEl.parentNode,
        nextSibling: modalEl.nextSibling,
      };
      document.body.appendChild(modalEl);
      modalEl.classList.add('modal-mask--body-portal-ready');
      this.portalReady = true;
    },
    restorePortalDom() {
      const el = this.$refs.modalRoot;
      if (!el || !this._portalOrigin?.parent) return;
      if (el.parentNode !== document.body) return;

      const { parent, nextSibling } = this._portalOrigin;
      if (nextSibling && nextSibling.parentNode === parent) {
        parent.insertBefore(el, nextSibling);
      } else {
        parent.appendChild(el);
      }
      this._portalOrigin = null;
    },
  },
};
</script>

<style scoped lang="scss">
.modal-container--full-width {
  align-items: center;
  border-radius: 0;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.modal-mask.right-aligned {
  justify-content: flex-end;

  .modal-container {
    border-radius: 0;
    height: 100%;
    width: 42rem;
  }
}
.modal-big {
  width: 60%;
}
.modal-medium {
  width: 55%;
}
.modal-small {
  width: 50%;
}
</style>
